import { useCan } from '@features/auth';
import { absoluteTime, relativeTime } from '@lib/time';
import { Badge, Button, Card, CardHeader, Toggle, WriteError } from '@shared/ui';

import { environmentDefaultWriteFrom, overrideSeedFrom } from '../api/environment-default-write';
import { useClearEnvironmentDefault, useSetEnvironmentDefault } from '../hooks/use-flags';
import {
    ENVIRONMENT_DESCRIPTION,
    ENVIRONMENT_LABEL,
    ENVIRONMENT_ORDER,
} from '../schemas/environment';

import type { FeatureFlagDefault, FlagEnvironment, FlagEnvironmentDefaults } from '../schemas/flag';

/**
 * The four backend-environment partitions (ADR 0007 Option B), each shown next
 * to what it would resolve to if nobody had touched it: the platform default.
 *
 * ## Why a column can create its own row with one click, no dialog
 *
 * Unlike a museum override (`OverrideDialog`), the key an environment write
 * addresses is never typed — it comes from `ENVIRONMENT_ORDER`, a closed set of
 * four. There is no typo to confirm and nothing to look up, so the two-step
 * "type it, then confirm it" flow that protects a hand-entered tenant id has
 * nothing here to protect against. A column that inherits starts overriding at
 * whatever the platform default currently resolves to — `enabled`,
 * `rolloutPercentage`, and `archived` all copied via `overrideSeedFrom`
 * (see `api/environment-default-write.ts`), so the new row resolves
 * identically to what the column was inheriting a moment ago and the write
 * changes no behaviour by itself — so the first click is safe to fire
 * immediately, the same way the platform-default toggles above fire on click
 * rather than behind a confirm step. Seeding only part of the row would break
 * that premise (a dropped rollout gate is a real behaviour change), which is
 * exactly why the seed is a named, tested function rather than inlined here.
 */
export function EnvironmentMatrix({
    flagKey,
    platformDefault,
    environmentDefaults,
}: {
    flagKey: string;
    platformDefault: FeatureFlagDefault;
    environmentDefaults: FlagEnvironmentDefaults;
}) {
    return (
        <Card>
            <CardHeader
                title="Environment defaults"
                subtitle="Overrides the platform default for one backend environment"
            />
            <div className="grid grid-cols-1 gap-px bg-surface-inset sm:grid-cols-2 lg:grid-cols-4">
                {ENVIRONMENT_ORDER.map((environment) => (
                    <EnvironmentColumn
                        key={environment}
                        flagKey={flagKey}
                        environment={environment}
                        row={environmentDefaults[environment]}
                        platformDefault={platformDefault}
                    />
                ))}
            </div>
        </Card>
    );
}

function EnvironmentColumn({
    flagKey,
    environment,
    row,
    platformDefault,
}: {
    flagKey: string;
    environment: FlagEnvironment;
    row: FeatureFlagDefault | undefined;
    platformDefault: FeatureFlagDefault;
}) {
    const setEnvironmentDefault = useSetEnvironmentDefault(flagKey);
    const clearEnvironmentDefault = useClearEnvironmentDefault(flagKey);
    const busy = setEnvironmentDefault.isPending || clearEnvironmentDefault.isPending;
    const error = setEnvironmentDefault.error ?? clearEnvironmentDefault.error;
    const label = ENVIRONMENT_LABEL[environment];
    // Matches `@RequiresRole('engineer')` on `PUT`/`DELETE
    // /:key/default/:environment` — same tier as the platform default's own
    // controls in `FlagDetailPage.tsx`, since this route is the same category
    // of write (ADR 0007 Option B), just a narrower partition.
    const canSetEnvironmentDefault = useCan('engineer');

    return (
        <div className="flex flex-col gap-3 bg-surface px-4 py-4">
            <div className="flex items-center justify-between gap-2">
                <span>
                    <span
                        className="text-[13px] font-semibold text-content-strong"
                        title={ENVIRONMENT_DESCRIPTION[environment]}
                    >
                        {label}
                    </span>
                    {/* The tooltip above is invisible on touch, keyboard nav,
                        and most screen readers — `eph` in particular is easy
                        to misread as one stage rather than the collective
                        partition it is, so its meaning needs a channel that
                        does not depend on hover. */}
                    <span className="block text-[11px] text-content-faint">
                        {ENVIRONMENT_DESCRIPTION[environment]}
                    </span>
                </span>
                <Badge tone={row ? 'brand' : 'neutral'}>{row ? 'Own value' : 'Inherited'}</Badge>
            </div>

            {row ? (
                <>
                    <Toggle
                        on={row.enabled}
                        busy={busy}
                        disabled={row.archived}
                        showLabel
                        onChange={
                            canSetEnvironmentDefault
                                ? (next) => {
                                      setEnvironmentDefault.mutate({
                                          environment,
                                          ...environmentDefaultWriteFrom(row, { enabled: next }),
                                      });
                                  }
                                : undefined
                        }
                        aria-label={`${label} default for ${flagKey}`}
                    />
                    <p className="text-[12px] text-content-muted">
                        {row.rolloutPercentage === undefined
                            ? 'No rollout gate.'
                            : `Gated at ${String(row.rolloutPercentage)}%.`}
                    </p>

                    <div className="flex items-center gap-2.5 border-t border-line-subtle pt-3">
                        <Toggle
                            on={row.archived}
                            busy={busy}
                            onChange={
                                canSetEnvironmentDefault
                                    ? (next) => {
                                          setEnvironmentDefault.mutate({
                                              environment,
                                              ...environmentDefaultWriteFrom(row, {
                                                  archived: next,
                                              }),
                                          });
                                      }
                                    : undefined
                            }
                            aria-label={`Archived state for the ${label} default of ${flagKey}`}
                        />
                        <span className="text-[12px] text-content-muted">Archived</span>
                    </div>

                    <p className="text-[11px] text-content-faint">
                        v{row.version} · {row.updatedBy}{' '}
                        <span title={absoluteTime(row.updatedAt)}>
                            {relativeTime(row.updatedAt)}
                        </span>
                    </p>

                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !canSetEnvironmentDefault}
                        onClick={() => {
                            clearEnvironmentDefault.mutate({ environment });
                        }}
                    >
                        Clear — inherit platform default
                    </Button>
                </>
            ) : (
                <>
                    <p className="text-[12px] leading-relaxed text-content-muted">
                        Inherits the platform default —{' '}
                        <strong className="font-semibold text-content-body">
                            {/* Resolution short-circuits an archived flag to
                                `false` before the rollout gate is even
                                consulted (`flag-resolution.ts`) — so an
                                archived-but-`enabled` platform default still
                                resolves "off" here, not "on". */}
                            {!platformDefault.archived && platformDefault.enabled ? 'on' : 'off'}
                        </strong>
                        {!platformDefault.archived &&
                            platformDefault.rolloutPercentage !== undefined &&
                            `, gated at ${String(platformDefault.rolloutPercentage)}%`}
                        .
                    </p>
                    {platformDefault.archived && (
                        <p className="text-[12px] leading-relaxed text-warning-muted">
                            The platform default is archived, so this column resolves off regardless
                            of what an override would say.
                        </p>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || platformDefault.archived || !canSetEnvironmentDefault}
                        onClick={() => {
                            setEnvironmentDefault.mutate({
                                environment,
                                ...environmentDefaultWriteFrom(
                                    undefined,
                                    overrideSeedFrom(platformDefault),
                                ),
                            });
                        }}
                    >
                        Override {label}
                    </Button>
                </>
            )}

            {error && <WriteError error={error} />}
        </div>
    );
}
